import { AppEnv } from 'App/utils';

/**
 * Resolves which banner (if any) should be shown for the current environment,
 * how it should be coloured, and how it should be described.
 *
 * Model (PPL – "Workspace banner design changes"):
 *  1. Simple build environments (local / dev / test) show their fixed associated
 *     colour (the `*-mode` SCSS classes in `_emil.scss`); the slug is ignored.
 *  2. A slug carrying a sandbox marker (`sandbox` / `sandboxtest` /
 *     `sandboxzurich`) shows the orange sandbox banner — regardless of env.
 *  3. A production build (`prod` / `prodfunk` / `prodzurich` / `prodzurichaxa`) with no reserved
 *     marker is plain client production → no banner.
 *  4. Production and staging builds with a non-sandbox reserved marker — or any
 *     staging build — show a banner whose colour is generated deterministically
 *     from the slug (same slug ⇒ same colour), guaranteed visibly distinct from
 *     the known local/dev/test/sandbox colours.
 *
 * Reserved markers are matched against whole `-`-separated slug segments, so a
 * client whose name merely contains a keyword (`greatest`, `testbank`) is never
 * mistaken for an environment.
 *
 * This is intentionally the single seam that decides the banner.
 */

export interface GeneratedBannerStyle {
  backgroundColor: string;
  textColor: string;
}

export interface EnvironmentBanner {
  /** Whether a banner should be rendered at all. */
  visible: boolean;
  /** Predefined environments: colour comes from this SCSS class. */
  modeClass?: string;
  /** Generated environments: colour is applied inline. */
  generatedStyle?: GeneratedBannerStyle;
  /** The real environment name (from the build env), e.g. "Production". */
  environmentName?: string;
  /** How the workspace is presented (from the slug marker), e.g. "Sandbox". */
  presentedAs?: string;
}

/**
 * Simple build environments keep their fixed colours (the `*-mode` SCSS classes
 * in `_emil.scss`) and always show a banner, regardless of tenant slug.
 */
const BUILD_MODE_CLASS: Record<string, string> = {
  [AppEnv.local]: 'local-mode',
  [AppEnv.dev]: 'dev-mode',
  [AppEnv.test]: 'test-mode',
};

/** The sandbox family all share the existing orange `sandbox-mode` SCSS class. */
const SANDBOX_MODE_CLASS = 'sandbox-mode';

/**
 * Sandbox markers, in specificity order (most specific first). Matched by whole
 * `-`-segment first and then as a substring fallback, so both `funk-sandbox-prod`
 * and `banksandbox-prod` count as sandbox — consistent with the shared
 * microfrontend banner (`<emil-modeinfo>`), which matches sandbox as a substring.
 */
const SANDBOX_TOKENS: string[] = [ 'sandboxtest', 'sandboxzurich', 'sandbox' ];

/**
 * Non-sandbox markers, matched by whole `-`-segment ONLY (no substring), so a
 * client whose name merely contains one (`testbank`, `accenture`, `category`)
 * is never mistaken for an environment. Each renders a deterministic generated
 * colour.
 */
const NON_SANDBOX_TOKENS: string[] = [ 'integration', 'acceptance', 'testing', 'test', 'uat', 'cat', 'acc' ];

/** The real environment name per build env (first half of the banner text). */
const ENVIRONMENT_NAME: Record<string, string> = {
  [AppEnv.local]: 'Local',
  [AppEnv.dev]: 'Development',
  [AppEnv.test]: 'Test',
  [AppEnv.stage]: 'Staging',
  [AppEnv.stagezurich]: 'Staging(zurich)',
  [AppEnv.stagezurichaxa]: 'Staging(zurich)',
  [AppEnv.prod]: 'Production',
  [AppEnv.prodfunk]: 'Production(funk)',
  [AppEnv.prodzurich]: 'Production(zurich)',
  [AppEnv.prodzurichaxa]: 'Production(zurich)',
};

/** How a workspace is presented per reserved marker (second half of the text). */
const PRESENTED_AS: Record<string, string> = {
  sandbox: 'Sandbox',
  sandboxzurich: 'Sandbox',
  sandboxtest: 'Sandbox 2',
  integration: 'Integration',
  acceptance: 'Acceptance',
  uat: 'UAT',
  cat: 'CAT',
  acc: 'ACC',
  test: 'Test',
  testing: 'Testing',
};

const STAGING_ENVS: string[] = [ AppEnv.stage, AppEnv.stagezurich, AppEnv.stagezurichaxa ];
const PROD_FAMILY_ENVS: string[] = [ AppEnv.prod, AppEnv.prodfunk, AppEnv.prodzurich, AppEnv.prodzurichaxa ];

// ---------------------------------------------------------------------------
// Deterministic colour generation
// ---------------------------------------------------------------------------

/**
 * Deterministic string hash (djb2-style). Stable across runs, so the same slug
 * always maps to the same colour.
 */
const hashString = ( value: string ): number => {
  let hash = 0;
  for ( let i = 0; i < value.length; i++ ) {
    hash = ( ( hash << 5 ) - hash + value.charCodeAt( i ) ) | 0;
  }
  return hash;
};

const toHexChannel = ( value: number ): string => {
  const hex = Math.round( value * 255 ).toString( 16 );
  return hex.length === 1 ? `0${hex}` : hex;
};

/** Converts an HSL colour (h in [0,360), s/l in percent) to a #rrggbb string. */
const hslToHex = ( h: number, s: number, l: number ): string => {
  const sat = s / 100;
  const lig = l / 100;
  const a = sat * Math.min( lig, 1 - lig );
  const channel = ( n: number ): number => {
    const k = ( n + h / 30 ) % 12;
    return lig - a * Math.max( -1, Math.min( k - 3, 9 - k, 1 ) );
  };
  return `#${toHexChannel( channel( 0 ) )}${toHexChannel( channel( 8 ) )}${toHexChannel( channel( 4 ) )}`;
};

const hexToRgb = ( hex: string ): [ number, number, number ] => [
  parseInt( hex.slice( 1, 3 ), 16 ),
  parseInt( hex.slice( 3, 5 ), 16 ),
  parseInt( hex.slice( 5, 7 ), 16 ),
];

/** Straight-line distance between two colours in RGB space (0–441). */
const colorDistance = ( a: string, b: string ): number => {
  const [ ar, ag, ab ] = hexToRgb( a );
  const [ br, bg, bb ] = hexToRgb( b );
  return Math.sqrt( ( ar - br ) * ( ar - br ) + ( ag - bg ) * ( ag - bg ) + ( ab - bb ) * ( ab - bb ) );
};

/** Picks a readable text colour (dark ink or white) for a given background. */
const readableTextColor = ( hex: string ): string => {
  const [ r, g, b ] = hexToRgb( hex ).map( ( v ) => v / 255 );
  const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
  return luminance > 0.6 ? '#111111' : '#ffffff';
};

/** Colours of the known environments (local, dev, test, sandbox) to stay clear of. */
export const KNOWN_ENVIRONMENT_COLORS = [ '#00A09B', '#007BFF', '#B9E7FC', '#E56910' ];

const CANDIDATE_HUE_STEP = 15; // degrees, swept across the whole wheel
const CANDIDATE_LIGHTNESS_LEVELS = [ 38, 50, 62, 74 ]; // %
const CANDIDATE_SATURATION = 72; // %
const MIN_DISTANCE_FROM_KNOWN = 90; // never resemble a known-environment colour
const MIN_DISTANCE_BETWEEN_COLORS = 70; // generated colours stay distinct from each other

/**
 * Palette for generated (non-sandbox) environments. Candidates are swept across
 * the whole colour wheel and greedily kept only when far enough (in RGB space)
 * from every known-environment colour AND from every colour already chosen.
 * Built once at module load; deterministic (no Math.random).
 */
const buildGeneratedPalette = (): string[] => {
  const palette: string[] = [];
  for ( let hue = 0; hue < 360; hue += CANDIDATE_HUE_STEP ) {
    for ( const lightness of CANDIDATE_LIGHTNESS_LEVELS ) {
      const candidate = hslToHex( hue, CANDIDATE_SATURATION, lightness );
      const farFromKnown = KNOWN_ENVIRONMENT_COLORS.every(
        ( known ) => colorDistance( candidate, known ) >= MIN_DISTANCE_FROM_KNOWN );
      const farFromChosen = palette.every(
        ( chosen ) => colorDistance( candidate, chosen ) >= MIN_DISTANCE_BETWEEN_COLORS );
      if ( farFromKnown && farFromChosen ) {
        palette.push( candidate );
      }
    }
  }
  return palette;
};

const GENERATED_PALETTE = buildGeneratedPalette();

/**
 * Deterministic banner colour for a generated environment, keyed on the seed
 * (the tenant slug). Same seed ⇒ same colour; the result is always clearly
 * distinct from the known local/dev/test/sandbox colours.
 */
export const generateBannerStyle = ( seed: string ): GeneratedBannerStyle => {
  const index = Math.abs( hashString( seed ) ) % GENERATED_PALETTE.length;
  const backgroundColor = GENERATED_PALETTE[index];
  return { backgroundColor, textColor: readableTextColor( backgroundColor ) };
};

// ---------------------------------------------------------------------------
// Resolution
// ---------------------------------------------------------------------------

/**
 * Finds a reserved environment marker in a tenant slug. Sandbox markers win and
 * are matched by whole `-`-segment first, then by substring fallback; every
 * other marker is matched by whole segment only.
 */
const detectSlugToken = ( tenantSlug: string ): string | undefined => {
  const slug = tenantSlug.toLowerCase();
  const segments = slug.split( '-' );
  const sandbox = SANDBOX_TOKENS.find( ( token ) => segments.includes( token ) )
    ?? SANDBOX_TOKENS.find( ( token ) => slug.includes( token ) );
  if ( sandbox ) {
    return sandbox;
  }
  return NON_SANDBOX_TOKENS.find( ( token ) => segments.includes( token ) );
};

/**
 * Decides the banner for the current environment.
 *
 * @param appEnv     Build-level environment (`process.env.REACT_APP_CONFIG`).
 * @param tenantSlug Current workspace slug – its reserved marker (if any) says
 *                   how the workspace is presented (e.g. `funk-sandbox-prod`).
 */
export const resolveEnvironmentBanner = (
  appEnv: string,
  tenantSlug?: string,
): EnvironmentBanner => {
  const environmentName = ENVIRONMENT_NAME[appEnv];

  // 1. Simple build environments keep their fixed colour; the slug is ignored.
  if ( BUILD_MODE_CLASS[appEnv] ) {
    return { visible: true, modeClass: BUILD_MODE_CLASS[appEnv], environmentName };
  }

  const token = detectSlugToken( tenantSlug ?? '' );

  // 2. A sandbox marker shows the orange banner in any env, and the sandbox IS
  //    the environment name — we don't surface Production(funk)/Staging(zurich)
  //    for a sandbox workspace, just "Sandbox" / "Sandbox 2".
  if ( token && SANDBOX_TOKENS.includes( token ) ) {
    return { visible: true, modeClass: SANDBOX_MODE_CLASS, environmentName: PRESENTED_AS[token] };
  }

  // 3. Staging always shows a banner: a generated colour keyed on the slug,
  //    with the marker (if any) describing how the workspace is presented.
  if ( STAGING_ENVS.includes( appEnv ) ) {
    return {
      visible: true,
      generatedStyle: generateBannerStyle( ( tenantSlug ?? appEnv ).toLowerCase() ),
      environmentName,
      presentedAs: token ? PRESENTED_AS[token] : undefined,
    };
  }

  // 4. Production: a non-sandbox reserved marker gets a generated colour; a
  //    plain client production slug (no marker) shows no banner.
  if ( PROD_FAMILY_ENVS.includes( appEnv ) ) {
    if ( token ) {
      return {
        visible: true,
        generatedStyle: generateBannerStyle( ( tenantSlug ?? '' ).toLowerCase() ),
        environmentName,
        presentedAs: PRESENTED_AS[token],
      };
    }
    return { visible: false };
  }

  // Unknown build environment (not expected with the fixed AppEnv enum).
  return { visible: false };
};
